import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";

const HEADLINES = [
  "Pay As You Go. Bring Your Own Keys.",
  "One Creative Workspace. Your Provider Billing.",
  "Premium Video & Ads — On Your Terms.",
];

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/2ed9bb58-39cb-4164-b754-f91a45d29180/images/9a6624f66dd0854a13018cf6e84bbbfc26800ea36fe78f1fae116ee7ed6b5c83.png";

export default function Hero() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % HEADLINES.length), 4200);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-end pt-32 pb-20 md:pb-32"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        aria-hidden="true"
      />
      {/* Heavy overlay for legibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#080808]/85 via-[#080808]/70 to-[#080808]" aria-hidden="true" />
      {/* Vignette accent */}
      <div className="absolute -left-40 top-1/4 w-[640px] h-[640px] rounded-full bg-[#D4AF37]/10 blur-[160px]" aria-hidden="true" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-[#D4AF37]" />
          <span data-testid="hero-eyebrow" className="text-[11px] md:text-xs font-bold uppercase tracking-[0.32em] text-[#D4AF37]">
            A Young Black Man Incorporated Company
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display text-[44px] sm:text-6xl md:text-7xl lg:text-[88px] font-black leading-[0.98] tracking-tighter text-[#F4F4F0] max-w-5xl"
        >
          <AnimatePresence mode="wait">
            <motion.span
              key={HEADLINES[index]}
              initial={{ opacity: 0, y: 18, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -18, filter: "blur(8px)" }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="block"
            >
              {HEADLINES[index]}
            </motion.span>
          </AnimatePresence>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="mt-8 max-w-2xl text-lg md:text-xl text-[#A0A0A0] leading-relaxed"
        >
          The ready-made creative workspace for commercials, brand promos, music videos and social ads.
          Start with <span className="text-[#F4F4F0]">pay-as-you-go credits</span> — no subscription to try it.
          Go <span className="text-[#F4F4F0]">Premium</span> to connect your own OpenAI, Gemini, Anthropic and ElevenLabs keys and pay providers directly for usage.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
        >
          <Link
            to="/register"
            data-testid="hero-primary-cta"
            className="group inline-flex items-center justify-center gap-2 gold-sheen font-bold px-8 py-4 transition-all shadow-[0_8px_32px_-8px_rgba(212,175,55,0.6)]"
          >
            Start Free — Try Pay-As-You-Go
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <a
            href="#byok-explainer"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center justify-center gap-2 text-[#F4F4F0] border border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 px-8 py-4 transition-colors"
          >
            <Play className="w-4 h-4" />
            See How BYOK Works
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-2 text-xs uppercase tracking-[0.18em] text-[#A0A0A0]"
        >
          <span data-testid="hero-trust-1">Pay-As-You-Go</span>
          <span className="w-1 h-1 rounded-full bg-[#D4AF37]" />
          <span data-testid="hero-trust-2">Bring Your Own Keys</span>
          <span className="w-1 h-1 rounded-full bg-[#D4AF37]" />
          <span data-testid="hero-trust-3">Cancel Anytime</span>
          <span className="w-1 h-1 rounded-full bg-[#D4AF37]" />
          <span data-testid="hero-trust-4">USD · Global</span>
        </motion.div>
      </div>
    </section>
  );
}
